import { setSearchTimestamp } from '@/actions/searchActions';
import { refreshVegnett } from '@/actions/vegnettActions';
import { refreshVegobjektSearch } from '@/actions/vegobjekterActions';
import { executeAllSearches } from '@/middleware/fetchVegobjekter';
import { stateSearchTimestamp } from '@/selectors/selectors';
import { useDispatch, useSelector } from '@/state/store';
import { useCallback, useState } from 'react';
import useDebounce from '../../../utils/useDebounce';
import SimpleToggle from '../../objects/SimpleToggle';

export const DateFilter = ({ className = '' }: { className?: string }) => {
    const timestamp = useSelector(stateSearchTimestamp);
    const [internal, setInternal] = useState(timestamp || new Date());
    const [enable, setEnable] = useState(timestamp != null);
    const [enterKeyPressed, setEnterKeyPressed] = useState(false);
    const [date, setDate] = useState(internal.toLocaleDateString('en-CA'));
    const dispatch = useDispatch();

    const onTimestampChange = useCallback(
        (value: Date | null) => {
            dispatch(setSearchTimestamp(value));
            if (value == null || isValidDate(value)) {
                dispatch(refreshVegnett());
                dispatch(refreshVegobjektSearch());
            }
        },
        [dispatch]
    );
    const debouncedUpdate = useDebounce(onTimestampChange, 350);

    const toggle = () => {
        const isEnabled = !enable;
        debouncedUpdate(isEnabled ? internal : null);
        setEnable(isEnabled);
    };

    const handleEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            setEnterKeyPressed(true);
            event.currentTarget.blur();
            dispatch(executeAllSearches());
        }
    };

    const handleBlur = () => {
        if (!enterKeyPressed) {
            dispatch(executeAllSearches());
        } else {
            setEnterKeyPressed(false);
        }
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const newDate = input.value;
        const dateObj = new Date(newDate);

        setDate(newDate);

        if (!isNaN(dateObj.getTime())) {
            setInternal(dateObj);
            if (enable) debouncedUpdate(dateObj);
        }
    };

    const handleClearDate = () => {
        const today = new Date();
        setDate(today.toISOString().split('T')[0]);
        setInternal(today);
        if (enable) debouncedUpdate(today);
    };

    return (
        <div className={'c-date-selector ' + className} role={'widget'}>
            <div className={'c-dates'}>
                <SimpleToggle
                    className={'c-dates__toggle'}
                    checked={enable}
                    label={'Søk basert på dato'}
                    id={'seach_date_toggle'}
                    onChange={toggle}
                />
                {enable && (
                    <div className="dato-container">
                        <input
                            aria-label="Date"
                            type="date"
                            disabled={!enable}
                            value={date}
                            onChange={handleDateChange}
                            className="date-input"
                            onKeyDown={handleEnter}
                            onBlur={handleBlur}
                            pattern="\d{4}-\d{2}-\d{2}"
                            min="1800-01-01"
                            max="9999-12-31"
                            required
                        />
                        {date && (
                            <button
                                className="clear-date"
                                onClick={handleClearDate}
                                aria-label="Tilbakestill dato"
                            >
                                &#x2715;
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

function isValidDate(maybeDate: Date) {
    const reGoodDate =
        /(([1][0-2]|[0][0-9])[-.\/]([0-2][0-9]|[3][0-1])[-.\/][1-9]([0-9]{3}))|([1-9]([0-9]{3})[-.\/]([1][0-2]|[0][0-9])[-.\/]([0-2][0-9]))/;
    return reGoodDate.test(maybeDate.toISOString().split('T')[0]);
}
